import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Heart, MessageCircle, Calendar, ShoppingBag, GraduationCap, Megaphone, MapPin, Sparkles, TrendingUp, ArrowRight, CheckCircle2, Phone, ChevronLeft, BarChart3, Briefcase, HeartHandshake, Brain, Coins, Cigarette } from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Legend,
  ComposedChart, Line, ReferenceLine, CartesianGrid,
} from 'recharts';
import { CATEGORIES } from '@/data/categories';
import { useLang } from '@/context/LanguageContext';

const SITE = 'https://www.matchgroupid.com';
const WA_NUMBER = '6281318719105';

function slugify(name) {
  return (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
}

function findCategoryBySlug(slug) {
  return CATEGORIES.find(c => slugify(c.name) === slug) || null;
}

function useSeoTags({ title, description, canonical }) {
  useEffect(() => {
    const prev = document.title;
    document.title = title;
    const setMeta = (name, content, isProp = false) => {
      const attr = isProp ? 'property' : 'name';
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    const setLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
      el.setAttribute('href', href);
    };
    setMeta('description', description);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:url', canonical, true);
    setLink('canonical', canonical);
    return () => { document.title = prev; };
  }, [title, description, canonical]);
}

// ─── Fake live-join names ───────────────────────────────────
const FAKE_NAMES = ['Andi', 'Budi', 'Cindy', 'Dewi', 'Eka', 'Fajar', 'Gita', 'Hendra', 'Ika', 'Joko', 'Kartika', 'Lita', 'Maya', 'Nanda', 'Okta', 'Putri', 'Rendy', 'Sari', 'Tono', 'Umar', 'Vina', 'Widi', 'Yani', 'Zaki', 'Aditya', 'Bella', 'Cakra', 'Dinda', 'Erik', 'Felicia'];
const FAKE_CITIES = ['Jakarta', 'Surabaya', 'Medan', 'Bandung', 'Bali', 'Semarang', 'Yogyakarta', 'Palembang', 'Makassar', 'Batam', 'Kupang', 'Manado', 'Balikpapan', 'Pontianak', 'Pekanbaru'];

function makeFakeJoin() {
  const name = FAKE_NAMES[Math.floor(Math.random() * FAKE_NAMES.length)];
  const initial = name.charAt(0) + '***' + name.slice(-1);
  const city = FAKE_CITIES[Math.floor(Math.random() * FAKE_CITIES.length)];
  const minsAgo = 1 + Math.floor(Math.random() * 55);
  return { id: Date.now() + Math.random(), name: initial, city, minsAgo };
}

export default function CategoryLandingPage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const isID = lang !== 'en';
  const cat = useMemo(() => findCategoryBySlug(slug), [slug]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joins, setJoins] = useState([]);

  useSeoTags({
    title: cat ? `${cat.name} — Cari Jodoh ${cat.name.replace(' Match', '')} Terpercaya | MGI` : 'Kategori — Match Group Indonesia',
    description: cat ? (isID ? cat.detail_id : cat.detail_en) : 'Match Group Indonesia',
    canonical: `${SITE}/kategori/${slug}`,
  });

  useEffect(() => {
    if (!cat) return;
    setLoading(true);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/categories/stats/${slugify(cat.name)}`;
    fetch(url).then(r => r.json()).then(d => { setStats(d); setLoading(false); }).catch(() => setLoading(false));
  }, [cat]);

  // Live-join ticker: seed 3, then add one every 4-7s, cap at 5.
  useEffect(() => {
    if (!cat || !cat.tiktok) return;
    const initial = [makeFakeJoin(), makeFakeJoin(), makeFakeJoin()];
    setJoins(initial);
    const t = setInterval(() => {
      setJoins(prev => [makeFakeJoin(), ...prev].slice(0, 5));
    }, 4000 + Math.random() * 3000);
    return () => clearInterval(t);
  }, [cat]);

  if (!cat) return <Navigate to="/" replace />;

  const waMsg = `Halo Admin MGI, saya tertarik gabung ${cat.name}. Mohon info selanjutnya.`;
  const waUrl = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(waMsg)}`;
  const catColor = cat.color;
  const isUnderDev = !cat.tiktok;

  const pillars = [
    { key: 'group',   icon: Users,          en: 'Katalog Member', id: 'Katalog Member',
      desc_en: 'Access to all curated Buddhist / Catholic / etc profiles verified by admin',
      desc_id: `Akses katalog seluruh member ${cat.name} yang sudah dikurasi manual & diverifikasi admin` },
    { key: 'meet',    icon: Calendar,       en: `${cat.name.replace(' Match','')} Meet`, id: `${cat.name.replace(' Match','')} Meet`,
      desc_en: 'Regular offline meetups, retreats, and community events',
      desc_id: 'Meetup offline rutin, gathering & event komunitas — daftar dari aplikasi, konfirmasi kehadiran, dapatkan tempat & waktu' },
    { key: 'mart',    icon: ShoppingBag,    en: `${cat.name.replace(' Match','')} Mart`, id: `${cat.name.replace(' Match','')} Mart`,
      desc_en: 'Marketplace between members — buy, sell, offer services within the community',
      desc_id: 'Marketplace antar member — jual beli & jasa dalam komunitas dengan harga fair' },
    { key: 'mastery', icon: GraduationCap,  en: `${cat.name.replace(' Match','')} Mastery`, id: `${cat.name.replace(' Match','')} Mastery`,
      desc_en: 'Learning hub: lectures, courses (PPTX/PDF), and skill development',
      desc_id: 'Learning hub: materi ceramah, kursus (PPTX/PDF), pengembangan skill spiritual & profesional' },
    { key: 'media',   icon: Megaphone,      en: `${cat.name.replace(' Match','')} Media`, id: `${cat.name.replace(' Match','')} Media`,
      desc_en: 'Announcement feed — broadcasts, testimonials, community stories',
      desc_id: 'Feed pengumuman komunitas — broadcast, testimoni jodoh, cerita success stories' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-[#FFF9F5] to-white">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-lg bg-white/70 border-b border-slate-200/60">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900" data-testid="back-to-landing">
            <ChevronLeft className="w-4 h-4" /> {isID ? 'Kembali' : 'Back'}
          </Link>
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-500">
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: catColor }} />
            <span>{cat.name}</span>
          </div>
          {cat.tiktok ? (
            <a href={`https://www.tiktok.com/@${cat.tiktok}`} target="_blank" rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-black" data-testid="hdr-tiktok">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-.88-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.26a8.16 8.16 0 0 0 4.77 1.52V6.33a4.85 4.85 0 0 1-1.84-.36z"/></svg>
              @{cat.tiktok}
            </a>
          ) : (
            <span className="text-xs text-slate-400">{isID ? 'Segera Hadir' : 'Coming Soon'}</span>
          )}
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(60% 40% at 20% 20%, ${catColor}22, transparent), radial-gradient(50% 50% at 90% 30%, ${catColor}18, transparent)` }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-center">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white shadow-sm border border-slate-200 text-xs font-semibold text-slate-600 mb-6">
            <Sparkles className="w-3.5 h-3.5" style={{ color: catColor }} />
            <span>{isID ? 'Perjodohan Serius, Kurasi Admin' : 'Serious Matchmaking, Admin-Curated'}</span>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="text-4xl sm:text-6xl font-black tracking-tight text-slate-900 leading-tight">
            {cat.name}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="max-w-2xl mx-auto mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
            {isID ? cat.detail_id : cat.detail_en}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="hero-wa-cta"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-green-500 hover:bg-green-600 text-white font-bold shadow-lg shadow-green-500/25 transition-all hover:scale-[1.02]">
              <Phone className="w-4 h-4" />
              {isID ? 'Hubungi Admin via WhatsApp' : 'Contact Admin via WhatsApp'}
            </a>
            <Link to="/auth" data-testid="hero-join-cta"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-bold shadow-lg transition-all hover:scale-[1.02]">
              <Heart className="w-4 h-4" style={{ color: catColor }} />
              {isID ? 'Gabung Sekarang' : 'Join Now'}
            </Link>
          </motion.div>
          {/* Live join ticker */}
          {joins.length > 0 && (
            <div className="mt-10 max-w-md mx-auto" data-testid="live-join-ticker">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-2 flex items-center justify-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                {isID ? 'AKTIVITAS TERBARU' : 'RECENT ACTIVITY'}
              </div>
              <div className="relative h-[110px] overflow-hidden">
                <AnimatePresence initial={false}>
                  {joins.slice(0, 3).map((j, idx) => (
                    <motion.div key={j.id}
                      initial={{ opacity: 0, y: -12, scale: 0.95 }}
                      animate={{ opacity: 1 - idx * 0.25, y: idx * 34, scale: 1 - idx * 0.02 }}
                      exit={{ opacity: 0, y: 60 }}
                      transition={{ duration: 0.4 }}
                      className="absolute inset-x-0 mx-auto max-w-sm bg-white border border-slate-200 rounded-full pl-2 pr-4 py-1.5 shadow-sm flex items-center gap-2 text-xs">
                      <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center text-white text-[10px] font-bold" style={{ backgroundColor: catColor }}>
                        {j.name.charAt(0)}
                      </div>
                      <span className="text-slate-700"><b>{j.name}</b> {isID ? 'dari' : 'from'} <b>{j.city}</b> {isID ? 'baru saja gabung' : 'just joined'}</span>
                      <span className="text-slate-400 ml-auto">{j.minsAgo}m {isID ? 'lalu' : 'ago'}</span>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Under Development banner */}
      {isUnderDev && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 -mt-2 mb-4">
          <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 flex items-center gap-3">
            <Sparkles className="w-5 h-5 text-amber-600 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              <b>{isID ? 'Kategori ini sedang dibangun.' : 'This category is under development.'}</b>{' '}
              {isID ? 'Hubungi admin untuk daftar early access & dapatkan slot pertama.' : 'Contact admin for early-access slot.'}
            </div>
          </div>
        </div>
      )}

      {/* KENAPA GABUNG (Why Join) */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center mb-3">
          {isID ? 'Kenapa Harus Gabung?' : 'Why Should You Join?'}
        </h2>
        <p className="text-center text-slate-500 max-w-2xl mx-auto text-sm sm:text-base mb-10">
          {isID
            ? `Bukan sekadar aplikasi kenalan. ${cat.name} adalah ekosistem lengkap: teman komunitas, jodoh serius, marketplace, learning hub, dan media komunitas — semua dalam satu tempat.`
            : `Not just another dating app. ${cat.name} is a complete ecosystem: community, serious matchmaking, marketplace, learning hub, and community media — all in one place.`}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { key: 'manual-curation', en: 'Kurasi Manual — Bukan Asal Swipe', id: 'Kurasi Manual — Bukan Asal Swipe', d_en: 'Every profile is manually curated', d_id: 'Setiap profil dikurasi manual sebelum masuk katalog.', ic: CheckCircle2 },
            { key: 'community-focus', en: '100% Community-Focused', id: '100% Fokus Komunitas', d_en: 'Only members who fit your community', d_id: 'Hanya bertemu jomblo yang secara aktif memilih komunitasmu — bukan random.', ic: Users },
          ].map((r) => (
            <div key={r.key} className="bg-white rounded-2xl border border-slate-200 p-5 hover:shadow-md transition-shadow">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${catColor}18` }}>
                <r.ic className="w-5 h-5" style={{ color: catColor }} />
              </div>
              <h3 className="font-bold text-slate-900 mb-1">{isID ? r.id : r.en}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{isID ? r.d_id : r.d_en}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 5 PILAR */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 text-center mb-3">
          {isID ? 'Yang Kamu Dapatkan' : 'What You Get'}
        </h2>
        <p className="text-center text-slate-500 max-w-2xl mx-auto text-sm sm:text-base mb-10">
          {isID ? '5 pilar inti untuk komunitas yang komplit.' : '5 core pillars for a complete community experience.'}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {pillars.map((p, i) => (
            <motion.div key={p.key}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col hover:shadow-lg hover:-translate-y-1 transition-all">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${catColor}15`, border: `1px solid ${catColor}30` }}>
                <p.icon className="w-5 h-5" style={{ color: catColor }} />
              </div>
              <h3 className="font-bold text-slate-900 mb-1 text-sm sm:text-base leading-tight">{isID ? p.id : p.en}</h3>
              <p className="text-xs text-slate-600 leading-relaxed">{isID ? p.desc_id : p.desc_en}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">
            <TrendingUp className="w-3.5 h-3.5" style={{ color: catColor }} />
            {isID ? 'DATA LIVE — TERSINKRON REALTIME' : 'LIVE DATA — REAL-TIME SYNC'}
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
            {isID ? `Statistik ${cat.name}` : `${cat.name} Statistics`}
          </h2>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-400">{isID ? 'Memuat data…' : 'Loading…'}</div>
        ) : !stats || stats.total === 0 ? (
          <div className="text-center py-12 bg-white border border-dashed border-slate-300 rounded-2xl">
            <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <div className="text-slate-500 text-sm">{isID ? 'Belum ada member di kategori ini. Jadilah yang pertama!' : 'No members yet. Be the first!'}</div>
          </div>
        ) : (
          <>
            {/* Big number cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
              <StatCard label={isID ? 'Total Member' : 'Total Members'} value={stats.total} accent={catColor} icon={Users} testId="stat-total" />
              <StatCard label={isID ? 'Laki-Laki' : 'Male'} value={stats.male_count} accent="#3B82F6" icon={Users} testId="stat-male" />
              <StatCard label={isID ? 'Perempuan' : 'Female'} value={stats.female_count} accent="#EC4899" icon={Users} testId="stat-female" />
              <StatCard label={isID ? 'Rasio L:P' : 'Ratio M:F'} value={stats.ratio_label} accent={catColor} icon={TrendingUp} testId="stat-ratio" />
            </div>

            {/* Gender bar */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6" data-testid="stat-gender-bar">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">{isID ? 'Distribusi Gender' : 'Gender Distribution'}</div>
              <div className="flex h-4 rounded-full overflow-hidden bg-slate-100">
                {stats.total > 0 && (
                  <>
                    <div style={{ width: `${(stats.male_count / stats.total) * 100}%`, backgroundColor: '#3B82F6' }} title={`${stats.male_count} Laki-Laki`} />
                    <div style={{ width: `${(stats.female_count / stats.total) * 100}%`, backgroundColor: '#EC4899' }} title={`${stats.female_count} Perempuan`} />
                  </>
                )}
              </div>
              <div className="flex justify-between mt-2 text-xs text-slate-600">
                <span>♂ {stats.male_count} ({stats.total ? Math.round((stats.male_count / stats.total) * 100) : 0}%)</span>
                <span>♀ {stats.female_count} ({stats.total ? Math.round((stats.female_count / stats.total) * 100) : 0}%)</span>
              </div>
            </div>

            {/* City + Country */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              <BarList title={isID ? 'Top Negara' : 'Top Countries'} items={stats.by_country} accent={catColor} icon={MapPin} testId="stat-countries" />
              <BarList title={isID ? 'Top Kota' : 'Top Cities'} items={stats.by_city} accent={catColor} icon={MapPin} testId="stat-cities" />
            </div>

            {/* Age stats — Bell Curve (normal distribution) overlay on histogram */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="stat-age">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{isID ? 'Distribusi Usia' : 'Age Distribution'}</div>
                  <div className="text-lg font-bold text-slate-900">{stats.age.min}-{stats.age.max} {isID ? 'thn' : 'yr'}</div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <MiniStat label={isID ? 'Rata-rata' : 'Mean'}   value={stats.age.mean} />
                  <MiniStat label={isID ? 'Median' : 'Median'}     value={stats.age.median} />
                  <MiniStat label={isID ? 'Modus' : 'Mode'}         value={stats.age.mode} />
                  <MiniStat label={isID ? 'Std Dev' : 'Std Dev'}   value={stats.age.stddev} />
                </div>
              </div>

              {/* Normal-distribution curve + per-year bars, using ComposedChart (Recharts) */}
              {(() => {
                const age = stats.age || {};
                const curve = age.normal_curve || [];
                if (!curve.length) return null;
                const histLookup = new Map();
                (age.histogram || []).forEach((b) => {
                  const [lo, hi] = String(b.label).includes('+')
                    ? [parseInt(b.label, 10), 99]
                    : String(b.label).split('-').map(v => parseInt(v, 10));
                  const span = Math.min(hi, 90) - lo + 1;
                  for (let x = lo; x <= Math.min(hi, 90); x++) {
                    histLookup.set(x, span > 0 ? b.count / span : 0);
                  }
                });
                // Scale the PDF curve so its peak aligns with the tallest histogram bar,
                // making the curve visually comparable to the actual member counts.
                const maxBar = Math.max(...Array.from(histLookup.values()), 0.0001);
                const maxPdf = Math.max(...curve.map(p => p.y || 0), 0.0001);
                const scale = maxBar > 0 ? (maxBar / maxPdf) : 1;
                const data = curve.map(pt => ({
                  x: pt.x,
                  curve: (pt.y || 0) * scale,
                  bar: histLookup.get(pt.x) || 0,
                }));
                const mean = Math.round(age.mean || 0);
                const stddev = Math.max(1, Math.round(age.stddev || 0));
                return (
                  <div style={{ width: '100%', height: 240 }} data-testid="stat-age-bell-curve">
                    <ResponsiveContainer>
                      <ComposedChart data={data} margin={{ top: 20, right: 12, bottom: 4, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                        <XAxis dataKey="x" tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false}
                          label={{ value: isID ? 'Usia (tahun)' : 'Age (years)', position: 'insideBottom', offset: -2, fontSize: 10, fill: '#94A3B8' }} />
                        <YAxis tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} allowDecimals={false} />
                        <RTooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #E2E8F0' }}
                          formatter={(v, k) => k === 'curve'
                            ? [Number(v).toFixed(1), isID ? 'kurva normal (ekspektasi)' : 'normal curve (expected)']
                            : [Math.round(Number(v) * 10) / 10, isID ? 'member/th' : 'members/yr']} />
                        <Bar dataKey="bar" fill={catColor} fillOpacity={0.35} radius={[4, 4, 0, 0]} maxBarSize={22} name={isID ? 'Data aktual' : 'Actual'} />
                        <Line type="monotone" dataKey="curve" stroke={catColor} strokeWidth={2.8} dot={false} name={isID ? 'Kurva normal' : 'Normal curve'} />
                        <ReferenceLine x={mean} stroke="#16A34A" strokeDasharray="4 4" label={{ value: 'μ', position: 'top', fontSize: 11, fill: '#16A34A', fontWeight: 700 }} />
                        <ReferenceLine x={mean - stddev} stroke="#2563EB" strokeDasharray="2 4" label={{ value: '−σ', position: 'top', fontSize: 10, fill: '#2563EB' }} />
                        <ReferenceLine x={mean + stddev} stroke="#2563EB" strokeDasharray="2 4" label={{ value: '+σ', position: 'top', fontSize: 10, fill: '#2563EB' }} />
                        <ReferenceLine x={mean - 2 * stddev} stroke="#93C5FD" strokeDasharray="1 4" label={{ value: '−2σ', position: 'top', fontSize: 9, fill: '#93C5FD' }} />
                        <ReferenceLine x={mean + 2 * stddev} stroke="#93C5FD" strokeDasharray="1 4" label={{ value: '+2σ', position: 'top', fontSize: 9, fill: '#93C5FD' }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}

              {/* Bucket summary chips (kept as quick-glance totals) */}
              <div className="flex flex-wrap gap-2 justify-center mt-3">
                {(stats.age.histogram || []).map(bucket => (
                  <div key={bucket.label} className="flex flex-col items-center px-2.5 py-1.5 rounded-lg bg-slate-50 border border-slate-200 min-w-[54px]">
                    <div className="text-[9px] text-slate-500 font-semibold">{bucket.label}</div>
                    <div className="text-xs font-bold text-slate-800">{bucket.count}</div>
                  </div>
                ))}
              </div>

              <div className="text-[10px] text-slate-400 mt-3 text-center italic">
                {isID ? 'Distribusi mendekati kurva normal — mayoritas member usia produktif menikah.' : 'Distribution approaches normal curve — majority of members are in prime marriage age.'}
              </div>
            </div>
          </>
        )}
      </section>

      {/* DEEP STATISTICS */}
      {stats && stats.total > 0 && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-4 pb-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">
              <BarChart3 className="w-3.5 h-3.5" style={{ color: catColor }} />
              {isID ? 'ANALISIS MENDALAM' : 'DEEP ANALYTICS'}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">
              {isID ? 'Kenali Komunitas Ini Lebih Dalam' : 'Know This Community Deeper'}
            </h2>
          </div>

          {/* Age × Gender stacked histogram */}
          {stats.age_x_gender && stats.age_x_gender.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-4" data-testid="stat-age-gender">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-4 h-4" style={{ color: catColor }} />
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {isID ? 'Distribusi Usia × Gender' : 'Age × Gender'}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={stats.age_x_gender} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} />
                  <RTooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #E2E8F0' }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="male"   stackId="a" name={isID ? '♂ Laki-Laki' : '♂ Male'}   fill="#3B82F6" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="female" stackId="a" name={isID ? '♀ Perempuan' : '♀ Female'} fill="#EC4899" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Two-column: Marriage + Education donut/pie */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {stats.by_marriage_status && stats.by_marriage_status.length > 0 && (
              <PieCard title={isID ? 'Status Pernikahan' : 'Marriage Status'} data={stats.by_marriage_status} accent={catColor} icon={HeartHandshake} testId="stat-marriage" />
            )}
            {stats.by_education && stats.by_education.length > 0 && (
              <PieCard title={isID ? 'Tingkat Pendidikan' : 'Education Level'} data={stats.by_education} accent={catColor} icon={GraduationCap} testId="stat-education" />
            )}
          </div>

          {/* MBTI 16-bar */}
          {stats.by_mbti && stats.by_mbti.length > 0 && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-4" data-testid="stat-mbti">
              <div className="flex items-center gap-2 mb-3">
                <Brain className="w-4 h-4" style={{ color: catColor }} />
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {isID ? 'Kepribadian MBTI' : 'MBTI Personality'}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={stats.by_mbti} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} />
                  <RTooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }} contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #E2E8F0' }} />
                  <Bar dataKey="count" fill={catColor} radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Two-column: Occupation + Income horizontal bars */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {stats.by_occupation && stats.by_occupation.length > 0 && (
              <HBarCard title={isID ? 'Top Pekerjaan' : 'Top Occupations'} data={stats.by_occupation} accent={catColor} icon={Briefcase} testId="stat-occupation" />
            )}
            {stats.by_income && stats.by_income.length > 0 && (
              <HBarCard title={isID ? 'Rentang Penghasilan' : 'Income Range'} data={stats.by_income} accent={catColor} icon={Coins} testId="stat-income" />
            )}
          </div>

          {/* Two-column: Zodiac pie + Religion flow */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {stats.by_zodiac && stats.by_zodiac.length > 0 && (
              <PieCard title={isID ? 'Zodiak' : 'Zodiac'} data={stats.by_zodiac} accent="#8B5CF6" icon={Sparkles} testId="stat-zodiac" />
            )}
            {stats.by_religion_flow && stats.by_religion_flow.length > 0 && (
              <HBarCard title={isID ? 'Aliran Agama' : 'Religious Denomination'} data={stats.by_religion_flow} accent={catColor} icon={HeartHandshake} testId="stat-religion" />
            )}
          </div>

          {/* Self-rating radar + Lifestyle pills */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {stats.self_rating_avg && (stats.self_rating_avg.busyness > 0 || stats.self_rating_avg.commitment > 0 || stats.self_rating_avg.financial > 0 || stats.self_rating_avg.self_quality > 0) && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="stat-self-rating">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4" style={{ color: catColor }} />
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {isID ? 'Rata-rata Penilaian Diri (skala 1-10)' : 'Average Self-Rating (1-10)'}
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={240}>
                  <RadarChart data={[
                    { subject: isID ? 'Kesibukan' : 'Busyness',       A: stats.self_rating_avg.busyness },
                    { subject: isID ? 'Komitmen' : 'Commitment',       A: stats.self_rating_avg.commitment },
                    { subject: isID ? 'Finansial' : 'Financial',       A: stats.self_rating_avg.financial },
                    { subject: isID ? 'Kualitas Diri' : 'Self Quality', A: stats.self_rating_avg.self_quality },
                  ]}>
                    <PolarGrid stroke="#E2E8F0" />
                    <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: '#475569', fontWeight: 600 }} />
                    <PolarRadiusAxis domain={[0, 10]} tick={{ fontSize: 9, fill: '#94A3B8' }} axisLine={false} />
                    <Radar name={isID ? 'Rata-rata' : 'Average'} dataKey="A" stroke={catColor} fill={catColor} fillOpacity={0.35} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            )}
            {stats.lifestyle && (stats.lifestyle.smokes.yes + stats.lifestyle.smokes.no + stats.lifestyle.vapes.yes + stats.lifestyle.vapes.no + stats.lifestyle.tattoo.yes + stats.lifestyle.tattoo.no + stats.lifestyle.routine_worship.yes + stats.lifestyle.routine_worship.no) > 0 && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="stat-lifestyle">
                <div className="flex items-center gap-2 mb-3">
                  <Cigarette className="w-4 h-4" style={{ color: catColor }} />
                  <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {isID ? 'Gaya Hidup' : 'Lifestyle'}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'routine_worship', l_id: 'Ibadah Rutin', l_en: 'Regular Worship', good: true },
                    { key: 'smokes',          l_id: 'Merokok',        l_en: 'Smokes',           good: false },
                    { key: 'vapes',           l_id: 'Vape',           l_en: 'Vapes',            good: false },
                    { key: 'tattoo',          l_id: 'Bertato',        l_en: 'Has Tattoo',       good: null },
                  ].map(row => {
                    const d = stats.lifestyle[row.key];
                    const pct = d?.yes_pct || 0;
                    return (
                      <div key={row.key} className="rounded-xl border border-slate-200 p-3">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">{isID ? row.l_id : row.l_en}</div>
                        <div className="text-2xl font-black text-slate-900 tabular-nums">{pct}%</div>
                        <div className="text-[10px] text-slate-400">{d?.yes || 0} / {(d?.yes || 0) + (d?.no || 0)} {isID ? 'menjawab Ya' : 'answered Yes'}</div>
                        <div className="mt-2 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: row.good === true ? '#10B981' : row.good === false ? '#EF4444' : catColor }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </section>
      )}


      {/* FINAL CTA */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 text-center">
        <div className="rounded-3xl p-10 sm:p-14 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${catColor}18, ${catColor}08)`, border: `1px solid ${catColor}30` }}>
          <Heart className="w-10 h-10 mx-auto mb-4" style={{ color: catColor }} />
          <h2 className="text-3xl sm:text-4xl font-black text-slate-900 leading-tight mb-3">
            {isID ? `Siap Menemukan Jodoh ${cat.name.replace(' Match', '')}?` : `Ready to Find Your ${cat.name.replace(' Match', '')} Match?`}
          </h2>
          <p className="text-slate-600 max-w-lg mx-auto mb-8">
            {isID ? 'Chat admin sekarang, dapatkan passkey gratis, dan mulai kenalan dengan komunitasmu.' : 'Chat admin now, get a free passkey, and start meeting your community.'}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <a href={waUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-wa-cta"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-green-500 hover:bg-green-600 text-white font-bold shadow-xl shadow-green-500/30 transition-all hover:scale-[1.03]">
              <Phone className="w-5 h-5" />
              {isID ? 'WhatsApp Admin' : 'WhatsApp Admin'}
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
          <p className="text-xs text-slate-500 mt-6">
            {isID ? 'Balasan admin: rata-rata < 30 menit (jam operasional 09:00 - 21:00 WIB)' : 'Admin response time: < 30 min (09:00 - 21:00 WIB)'}
          </p>
        </div>
      </section>
    </div>
  );
}

function StatCard({ label, value, accent, icon: Icon, testId }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 sm:p-5 relative overflow-hidden" data-testid={testId}>
      <div className="absolute -right-4 -top-4 w-16 h-16 rounded-full opacity-10" style={{ backgroundColor: accent }} />
      <Icon className="w-4 h-4 mb-2" style={{ color: accent }} />
      <div className="text-2xl sm:text-3xl font-black text-slate-900 tabular-nums">{value}</div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function BarList({ title, items, accent, icon: Icon, testId }) {
  const max = Math.max(...(items || []).map(i => i.count), 1);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</div>
      </div>
      {(items || []).length === 0 ? (
        <div className="text-xs text-slate-400 py-3 text-center">—</div>
      ) : (
        <div className="space-y-2">
          {(items || []).slice(0, 8).map(it => (
            <div key={it.name} className="text-xs">
              <div className="flex items-center justify-between mb-1">
                <span className="text-slate-700 font-medium truncate">{it.name}</span>
                <span className="text-slate-500 tabular-nums">{it.count}</span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${(it.count / max) * 100}%`, backgroundColor: accent, opacity: 0.85 }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="text-center">
      <div className="text-lg font-bold text-slate-900 tabular-nums">{value}</div>
      <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold mt-0.5">{label}</div>
    </div>
  );
}


const PIE_COLORS = ['#D98A94', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#EF4444'];

function PieCard({ title, data, accent, icon: Icon, testId }) {
  const total = data.reduce((s, d) => s + (d.count || 0), 0);
  const items = data.map((d, i) => ({ ...d, pct: total ? Math.round((d.count / total) * 100) : 0, fill: i === 0 ? accent : PIE_COLORS[(i + 1) % PIE_COLORS.length] }));
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
        <ResponsiveContainer width="100%" height={170}>
          <PieChart>
            <Pie data={items} dataKey="count" nameKey="name" innerRadius={40} outerRadius={70} paddingAngle={2}>
              {items.map((d) => <Cell key={d.name} fill={d.fill} />)}
            </Pie>
            <RTooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: '1px solid #E2E8F0' }} />
          </PieChart>
        </ResponsiveContainer>
        <div className="space-y-1">
          {items.slice(0, 6).map(it => (
            <div key={it.name} className="flex items-center gap-2 text-xs">
              <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: it.fill }} />
              <span className="text-slate-700 font-medium truncate flex-1">{it.name}</span>
              <span className="text-slate-500 tabular-nums">{it.count} · {it.pct}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function HBarCard({ title, data, accent, icon: Icon, testId }) {
  const max = Math.max(...(data || []).map(i => i.count), 1);
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</div>
      </div>
      <div className="space-y-2">
        {(data || []).map(it => (
          <div key={it.name} className="text-xs">
            <div className="flex items-center justify-between mb-1">
              <span className="text-slate-700 font-medium truncate">{it.name}</span>
              <span className="text-slate-500 tabular-nums">{it.count}</span>
            </div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${(it.count / max) * 100}%`, backgroundColor: accent, opacity: 0.85 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
